import ServerErrorHandler from 'shared/feedback/ErrorHandler';
import { dispatchMessage, EventType } from 'shared/feedback/toasts';
import { i18n } from 'shared/i18n';
import { ApiError, api, on404 } from 'shared/api/client';
import type { paths } from 'shared/api/generated/api';

type DesignDraftBody = NonNullable<paths['/design-draft/']['post']['requestBody']>['content']['application/json'];
type DesignDraftResponse = paths['/design-draft/{documentSuperId}']['get']['responses'][200]['content']['application/json'];
export type DesignDraft = Omit<DesignDraftResponse, 'content'> & { content: DraftStyleEntry[] | null };
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { documentKeys, type DraftStyleEntry } from 'entities/document';
import { styleKeys } from 'entities/style';
import { themeKeys } from 'entities/theme';
import { cancelPendingDesignDraftSaves } from 'shared/lib/pending-design-saves';
import { draftKeys } from './keys';

export const NO_DRAFT = 'NO_DRAFT';
const APPLY_DRAFT_TOAST_ID = 'apply-draft';

export const draftQueryKey = (documentSuperId: number | string) => draftKeys.detail(documentSuperId);

interface DraftDocumentRef {
  superId: number | string;
  revisionId?: string;
}

type DesignDraftPayload = {
  documentSuperId: number | string;
  [key: string]: unknown;
};

export function useDraft({ documentSuperId }: { documentSuperId: number | string }) {
  return useQuery({
    queryKey: draftKeys.detail(documentSuperId),

    queryFn: () =>
      api
        .GET('/design-draft/{documentSuperId}', { params: { path: { documentSuperId: Number(documentSuperId) } } })
        .then((res) => (res.data ? (res.data as DesignDraft) : NO_DRAFT))
        .catch(on404(NO_DRAFT)),

    placeholderData: keepPreviousData,
  });
}

export function useSaveDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: DesignDraftPayload) => {
      await api.POST('/design-draft/', { body: draft as DesignDraftBody });
    },

    onMutate: (draft) => {
      const key = draftKeys.detail(draft.documentSuperId);
      const previous = queryClient.getQueryData(key);
      queryClient.setQueryData(key, draft);
      return { previous, key };
    },

    onError: (err, _draft, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      ServerErrorHandler(err);
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...draftKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...themeKeys.appliedStatusAll],
      });
    },
  });
}

export const scssErrorDetail = (e: unknown): string | undefined => {
  if (!(e instanceof ApiError) || e.code !== 'INVALID_SCSS') return undefined;
  const { detail } = (e.details ?? {}) as { detail?: string };
  return detail?.split('\n')[0].trim() || undefined;
};

export function useApplyDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pDocument: DraftDocumentRef) => {
      await api.POST('/documents/{superId}/actions', {
        params: { path: { superId: Number(pDocument.superId) }, query: { compileSass: 'true' } },
        body: { action: 'apply_draft', revisionId: pDocument.revisionId },
      });
    },

    onMutate: () => {
      cancelPendingDesignDraftSaves();
    },

    onError: (e: unknown) => {
      const detail = scssErrorDetail(e);
      dispatchMessage(EventType.ADD_MESSAGE, {
        id: APPLY_DRAFT_TOAST_ID,
        type: 'error',
        title: i18n('theme:apply-draft.error-title'),
        text: i18n('theme:apply-draft.error-message', { detail, context: detail && 'detail' }),
      });
    },

    onSuccess: () => {
      dispatchMessage(EventType.ADD_MESSAGE, {
        id: APPLY_DRAFT_TOAST_ID,
        type: 'success',
        title: i18n('theme:apply-draft.success-title'),
        text: i18n('theme:apply-draft.success-message'),
        removeTime: 4000,
      });

      queryClient.invalidateQueries({
        queryKey: [...draftKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...themeKeys.appliedStatusAll],
      });
    },
  });
}

export function useDiscardDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pDocument: DraftDocumentRef) => {
      await api.DELETE('/design-draft/{documentSuperId}', { params: { path: { documentSuperId: Number(pDocument.superId) } } });
    },

    onMutate: () => {
      cancelPendingDesignDraftSaves();
    },

    onError: ServerErrorHandler,

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...draftKeys.all],
      });
      queryClient.invalidateQueries({
        queryKey: [...documentKeys.detailAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...styleKeys.cssAll],
      });
      queryClient.invalidateQueries({
        queryKey: [...themeKeys.appliedStatusAll],
      });
    },
  });
}
